import {
  BodyExtraSmall,
  Flex,
  H6,
  InlineSpinner,
  ProgressBar,
  Stepper,
} from '@taxbit-private/cosmic'
import {
  COSMIC_VALUE_PLACEHOLDER,
  useCosmicLocalizationContext,
} from '@taxbit-private/cosmic-localization'
import { useMemo } from 'react'
import styled from 'styled-components'

const ItalicBodyExtraSmall = styled(BodyExtraSmall)`
  font-style: italic;
`

type ProgressData = {
  /** Progress as a percentage, number between 0 and 100 */
  progress: number
  remainingSeconds: number
  elapsedSeconds: number
  isFinalizing: boolean
}

type InProgressStateStepperProps = {
  progressData: ProgressData | null
  lastSuccessfulUpdate?: string | null
  currentDagRunStartTime?: string | null
}

const InProgressStateStepper = ({
  progressData,
  lastSuccessfulUpdate,
  currentDagRunStartTime,
}: InProgressStateStepperProps) => {
  const { formatDateTime, formatNumber, formatRelativeDateTime } = useCosmicLocalizationContext()

  const steps = useMemo(() => {
    const updatingContent = progressData ? (
      <Flex direction="column" gap="s">
        <ProgressBar variant="primary" value={progressData.progress} />

        <H6 colorVariant="secondary">
          {progressData.isFinalizing
            ? 'Finalizing...'
            : `${formatNumber({ value: progressData.progress / 100, format: 'Percentage' })} Complete`}
        </H6>
      </Flex>
    ) : (
      <></>
    )

    const updatingTitleUtility =
      progressData && currentDagRunStartTime ? (
        <Flex alignItems="top" gap="s">
          {!progressData.isFinalizing && (
            <ItalicBodyExtraSmall colorVariant="secondary">
              {formatRelativeDateTime({
                date: new Date(Date.now() + progressData.remainingSeconds * 1000),
              })}
            </ItalicBodyExtraSmall>
          )}
          <InlineSpinner />
        </Flex>
      ) : (
        <></>
      )

    return [
      {
        key: 'last-successful-update',
        title: 'Last successful update',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {lastSuccessfulUpdate
              ? formatDateTime({
                  date: lastSuccessfulUpdate,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: <></>,
        avatarProps: { iconName: 'check-circle' as const, variant: 'success' as const },
      },
      {
        key: 'updating',
        title: 'Updating',
        titleUtilityElement: updatingTitleUtility,
        titleUtilityElementPlacement: 'right' as const,
        content: updatingContent,
        avatarProps: { iconName: 'refresh' as const, variant: 'primary' as const },
      },
    ]
  }, [
    progressData,
    lastSuccessfulUpdate,
    currentDagRunStartTime,
    formatDateTime,
    formatNumber,
    formatRelativeDateTime,
  ])

  return <Stepper currentStep={1} steps={steps} />
}

export default InProgressStateStepper
